import { useEffect, useState } from 'react';

/**
 * Height (px) of the regular kiosk's sticky core-menu header, kept fresh with a
 * ResizeObserver so orientation and viewport changes re-measure. Returns 0 when
 * disabled or when no header exists (and follows the header when it mounts late or is replaced). Kiosk Pro
 * has no such header: its layout passes the selector of its fixed nav instead.
 */
export function useKioskHeaderOffset(enabled: boolean, selector = 'header[data-testid="dispensary-header"]'): number {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setOffset(0);
      return undefined;
    }
    let header: HTMLElement | null = null;
    let resizeObserver: ResizeObserver | undefined;
    let frame: number | undefined;
    const mutationObserver = new MutationObserver(() => schedule());
    // The header can mount late or be replaced (remount, layout swap, route
    // change), so re-find it on DOM changes and re-bind the resize observer.
    // Once found, only the direct children of its ancestors are watched (so replacing the
    // header or any container above it is seen): the chat under body mutates deeply while
    // messages stream and must not trigger a lookup.
    // Until then, watch body deeply so a late mount is seen.
    const observeRoot = (): void => {
      mutationObserver.disconnect();
      if (header?.parentElement) {
        for (let node: HTMLElement | null = header.parentElement; node; node = node.parentElement) {
          mutationObserver.observe(node, { childList: true });
        }
      } else {
        mutationObserver.observe(document.body, { childList: true, subtree: true });
      }
    };
    const bind = (): void => {
      const found = document.querySelector<HTMLElement>(selector);
      if (found === header) return;
      resizeObserver?.disconnect();
      resizeObserver = undefined;
      header = found;
      observeRoot();
      setOffset(found?.offsetHeight ?? 0);
      if (!found) return;
      resizeObserver = new ResizeObserver(() => {
        if (found === header) setOffset(found.offsetHeight);
      });
      resizeObserver.observe(found);
    };
    // At most one lookup per frame, however many mutations arrive.
    function schedule(): void {
      if (frame !== undefined) return;
      frame = requestAnimationFrame(() => {
        frame = undefined;
        bind();
      });
    }
    observeRoot();
    bind();
    return () => {
      if (frame !== undefined) cancelAnimationFrame(frame);
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
    };
  }, [enabled, selector]);

  return enabled ? offset : 0;
}
